import axios, { AxiosResponse } from 'axios';
import apiClient from './apiClient';

// OpenAPI-compliant interfaces aligned with API specification
export interface JobTypePlatformInfo {
  id: number;
  name: string;
  platform_type: string;
}

export interface JobType {
  id: number;
  uuid?: string;
  code: string;
  name: string;
  description?: string;
  source_platform: number; // Mock server returns integer, not nested object
  target_platform: number; // Mock server returns integer, not nested object
  source_platform_name?: string; // Additional field from mock server
  target_platform_name?: string; // Additional field from mock server
  prompt?: string;
  is_extraction_only: boolean;
  schemas?: string; // Mock server returns string, not array
  extractors?: string; // Mock server returns string, not array
  loaders?: string; // Mock server returns string, not array
  jobtype_dependencies?: any; // Additional field from mock server
  created_at?: string;
  updated_at?: string;
  numericId?: number; // Add for frontend compatibility
  supportedPlatforms?: string[]; // Add for frontend compatibility
}

export interface JobTypeCreate {
  code?: string;
  name: string;
  description?: string;
  source_platform: number;
  target_platform: number;
  prompt?: string;
  is_extraction_only?: boolean;
}

export interface JobTypeListParams {
  source_platform?: number;
  target_platform?: number;
  is_extraction_only?: boolean;
}

// Legacy interface for backward compatibility
export interface ApiJobType {
  id: number;
  code: string;
  name: string;
  description: string;
  prompt: string;
  source_platform_id?: number | null;
  target_platform_id?: number | null;
  source_platform?: number | string;
  target_platform?: number | string;
  source_platform_name?: string;
  target_platform_name?: string;
  schemas?: (string | number)[];
  is_extraction_only?: boolean;
  extractors?: (string | number)[];
  loaders?: (string | number)[];
  created_at?: string;
  updated_at?: string;
}


// Interface for endpoint
export interface Endpoint {
  id: number;
  name: string;
  description: string;
  platform_id: number;
  platform_name: string;
  job_type_id: number;
  is_source: boolean;
  is_target: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PaginatedJobTypesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiJobType[];
}

export interface JobTypeUpdatePayload {
  name?: string;
  code?: string;
  description?: string | null;
  prompt?: string | null;
  source_platform_id?: number | null;
  target_platform_id?: number | null;
}

// Legacy normalization logic removed - using service JobType interface directly


class JobTypeService {
  // OpenAPI-compliant methods
  async getJobTypes(params?: JobTypeListParams): Promise<JobType[]> {
    try {
      const response = await apiClient.get<JobType[]>('/api/job-types/', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch job types:', error);
      throw this.handleError(error);
    }
  }

  async getAllJobTypes(params?: JobTypeListParams): Promise<JobType[]> {
    try {
      const response = await apiClient.get<{ count: number; results: JobType[] }>('/api/job-types/', { params });
      return response.data.results || response.data;
    } catch (error) {
      console.error('Failed to fetch job types:', error);
      return [];
    }
  }

  async createJobType(data: JobTypeCreate): Promise<JobType> {
    try {
      const response = await apiClient.post<JobType>('/api/job-types/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create job type:', error);
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      if (errorData && typeof errorData === 'object' && 'error' in errorData) {
        return new Error(errorData.error);
      }
      return new Error(`HTTP ${error.response.status}: ${error.response.statusText}`);
    }
    return error instanceof Error ? error : new Error('Unknown error occurred');
  }

}

export const jobTypeService = new JobTypeService();
